import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { login } from "../auth/authClient";

/**
 * Client-side gate. SWA's staticwebapp.config.json enforces the same rules
 * server-side (that's the real boundary) — this just avoids flashing a page
 * the user can't use, and gives a friendlier prompt than a bare 401.
 *
 * Cafeteria staff are a kiosk-style role: an account with the cafeteria role
 * (and not also admin) is locked to /cafeteria/counter and redirected away
 * from everything else, including the rest of the platform and their own
 * wallet page — the counter operator scans OTHER people's codes, they don't
 * need general platform access.
 */
export function ProtectedRoute({
  children,
  requireCoach = false,
  requireAdmin = false,
  requireCafeteria = false,
}: {
  children: ReactNode;
  requireCoach?: boolean;
  requireAdmin?: boolean;
  requireCafeteria?: boolean;
}) {
  const { isLoading, isAuthenticated, isCoach, isAdmin, isCafeteria } = useAuth();

  if (isLoading) {
    return <div className="state-panel">Checking your sign-in…</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="state-panel">
        <h2>Sign in required</h2>
        <p>Sign in with your Madinet Masr work account to continue.</p>
        <button className="btn btn-primary" onClick={() => login()}>
          Sign in
        </button>
      </div>
    );
  }

  if (isCafeteria && !isAdmin && !requireCafeteria) {
    return <Navigate to="/cafeteria/counter" replace />;
  }

  if (requireCoach && !isCoach) {
    return (
      <div className="state-panel">
        <h2>Coaches only</h2>
        <p>This page is limited to gym coaches. Talk to HR if you need access.</p>
      </div>
    );
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="state-panel">
        <h2>Admins only</h2>
        <p>This page is limited to gym administrators.</p>
      </div>
    );
  }

  if (requireCafeteria && !isCafeteria) {
    return (
      <div className="state-panel">
        <h2>Cafeteria staff only</h2>
        <p>This page is limited to cafeteria counter staff.</p>
      </div>
    );
  }

  return <>{children}</>;
}
